// src/pages/ManagementLookupPage.js
// Read-only personnel lookup for Management: search the exercise, open an
// officer, see their details and — if verified — their verification slip.
// There is deliberately nothing here that changes any record.

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Box,
  Button,
  TextField,
  InputAdornment,
  Typography,
  Chip,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Grid,
  Divider,
  IconButton,
  MenuItem,
} from '@mui/material';
import {
  Search as SearchIcon,
  ArrowBack as BackIcon,
  CheckCircle as CheckCircleIcon,
  HourglassEmpty as PendingIcon,
  Cancel as CancelIcon,
  PictureAsPdf as PdfIcon,
} from '@mui/icons-material';
import axios from 'axios';
import nisLogoWithText from '../assets/images/immgrationlogo1.png';

const authCfg = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
});

/** Date part only — never build a Date object, which would shift the day. */
const showDate = (value) => {
  if (!value) return '—';
  const m = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : '—';
};

const showDateTime = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  return isNaN(d.getTime()) ? '—' : d.toLocaleString('en-GB');
};

/* attendanceStatus defaults to DID_NOT_APPEAR for everyone, so it only means
   "absent" once the command has closed. Until then an unverified officer is
   simply not yet verified. */
const statusChip = (person) => {
  if (person.verificationStatus === 'VERIFIED') {
    return <Chip icon={<CheckCircleIcon />} label="Verified" color="success" size="small" />;
  }
  if (person.command?.status === 'COMPLETED' && person.attendanceStatus === 'DID_NOT_APPEAR') {
    return <Chip icon={<CancelIcon />} label="Did Not Appear" color="error" size="small" />;
  }
  return <Chip icon={<PendingIcon />} label="Not Yet Verified" color="warning" size="small" />;
};

const Field = ({ label, value }) => (
  <Grid item xs={12} sm={6} md={4}>
    <Typography variant="caption" color="text.secondary" display="block">{label}</Typography>
    <Typography variant="body2" fontWeight={500}>{value || '—'}</Typography>
  </Grid>
);

const ManagementLookupPage = () => {
  const navigate = useNavigate();

  const [exercises, setExercises] = useState([]);
  const [exerciseId, setExerciseId] = useState('');
  const [query, setQuery] = useState('');
  const [results, setResults] = useState(null);
  const [truncated, setTruncated] = useState(false);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState('');

  const [selected, setSelected] = useState(null);
  const [loadingOne, setLoadingOne] = useState(false);
  const [autoRan, setAutoRan] = useState(false);

  /* While an exercise is running there is nothing to choose between, so the
     selector stays hidden. Once it closes, past exercises become selectable. */
  const noActiveExercise = exercises.length > 0 && !exercises.some((e) => e.status === 'ACTIVE');

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get('/api/management/exercises', authCfg());
        const list = res.data.data.exercises || [];
        setExercises(list);
        const active = list.find((e) => e.status === 'ACTIVE');
        if (active) setExerciseId(active.id);
        else if (list.length) setExerciseId(list[0].id);
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to load exercises');
      }
    };
    load();
  }, []);

  /* The dashboard search bar sends the term across as ?q= — run it on arrival. */
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const id0 = params.get('id');
    if (id0 && !autoRan) {          // arrived from a chosen suggestion
      setAutoRan(true);
      openPerson(id0);
      return;
    }
    const q0 = params.get('q');
    if (q0 && exerciseId && !autoRan) {
      setQuery(q0);
      setAutoRan(true);
      runSearch(q0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exerciseId]);

  const runSearch = async (term) => {
    const q = (term !== undefined ? term : query).trim();
    if (q.length < 3) {
      setError('Enter at least 3 characters — a service number, IPPIS number or name');
      return;
    }
    setSearching(true);
    setError('');
    setSelected(null);
    try {
      const res = await axios.get(
        `/api/management/personnel/search?q=${encodeURIComponent(q)}&exerciseId=${exerciseId}`,
        authCfg()
      );
      setResults(res.data.data.results || []);
      setTruncated(res.data.data.truncated);
    } catch (err) {
      setError(err.response?.data?.message || 'Search failed');
      setResults(null);
    } finally {
      setSearching(false);
    }
  };

  const openPerson = async (id) => {
    setLoadingOne(true);
    setError('');
    try {
      const res = await axios.get(`/api/management/personnel/${id}`, authCfg());
      setSelected(res.data.data.personnel);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load officer');
    } finally {
      setLoadingOne(false);
    }
  };

  const apiBaseUrl = (process.env.REACT_APP_API_URL || 'http://localhost:5000').replace('/api', '');
  const pdfUrl = selected?.verification?.pdfUrl
    ? `${apiBaseUrl}${selected.verification.pdfUrl}`
    : null;

  return (
    <Container maxWidth="xl">
      <Box sx={{ mt: 4, mb: 6 }}>

        {/* Header */}
        <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
          <Box display="flex" alignItems="center" gap={2.5}>
            <IconButton onClick={() => navigate('/')}><BackIcon /></IconButton>
            <Box component="img" src={nisLogoWithText} alt="Nigeria Immigration Service"
              sx={{ height: 50, width: 'auto', objectFit: 'contain' }} />
            <Box>
              <Typography variant="h6" fontWeight="600"
                sx={{ color: '#00a651', lineHeight: 1.2, mb: 0.5, textTransform: 'uppercase',
                      letterSpacing: '0.5px', fontSize: '1.1rem' }}>
                Personnel Lookup
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>
                Search any officer in the exercise and see their verification status
              </Typography>
            </Box>
          </Box>
        </Paper>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>{error}</Alert>
        )}

        {/* Search bar */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box display="flex" gap={2} flexWrap="wrap" alignItems="center">
            {noActiveExercise && exercises.length > 0 && (
              <TextField select label="Exercise" value={exerciseId} size="small"
                onChange={(e) => { setExerciseId(e.target.value); setResults(null); setSelected(null); }}
                sx={{ minWidth: 280 }}>
                {exercises.map((ex) => (
                  <MenuItem key={ex.id} value={ex.id}>
                    {ex.name}{ex.status !== 'ACTIVE' ? ' (closed)' : ''}
                  </MenuItem>
                ))}
              </TextField>
            )}
            <TextField
              placeholder="Service number, IPPIS number, account number or name"
              value={query}
              onChange={(e) => {
                  setQuery(e.target.value);
                  if (e.target.value.trim().length < 3) { setResults(null); setSelected(null); }
                }}
              onKeyDown={(e) => { if (e.key === 'Enter') runSearch(); }}
              size="small"
              sx={{ flex: 1, minWidth: 300 }}
              InputProps={{
                startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>,
              }}
            />
            <Button variant="contained" onClick={() => runSearch()} disabled={searching}
              sx={{ textTransform: 'none', fontWeight: 'bold', px: 4 }}>
              {searching ? <CircularProgress size={22} /> : 'Search'}
            </Button>
          </Box>
        </Paper>

        {/* Results */}
        {results && !selected && (
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              {results.length === 0
                ? 'No officer found for that search'
                : `${results.length} officer${results.length > 1 ? 's' : ''} found${truncated ? ' (showing the first 50 — narrow your search)' : ''}`}
            </Typography>
            {results.length > 0 && (
              <TableContainer sx={{ maxHeight: 460 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>Service No.</TableCell>
                      <TableCell>IPPIS No.</TableCell>
                      <TableCell>Name</TableCell>
                      <TableCell>Rank</TableCell>
                      <TableCell>Command</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {results.map((p) => (
                      <TableRow key={p.id} hover>
                        <TableCell><strong>{p.serviceNumber}</strong></TableCell>
                        <TableCell>{p.ippisNumber}</TableCell>
                        <TableCell>{p.fullName}</TableCell>
                        <TableCell>{p.rankName}</TableCell>
                        <TableCell>
                          {p.command?.code}
                          <Typography variant="caption" color="text.secondary" display="block">
                            {p.command?.zone}
                          </Typography>
                        </TableCell>
                        <TableCell>{statusChip(p)}</TableCell>
                        <TableCell align="right">
                          <Button size="small" variant="outlined" onClick={() => openPerson(p.id)}
                            sx={{ textTransform: 'none' }}>
                            View
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        )}

        {loadingOne && (
          <Box display="flex" justifyContent="center" p={6}><CircularProgress size={50} /></Box>
        )}

        {/* One officer in full */}
        {selected && !loadingOne && (
          <>
            <Paper sx={{ p: 3, mb: 3 }}>
              <Box display="flex" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={2}>
                <Box>
                  <Typography variant="h5" fontWeight="bold">{selected.fullName}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {selected.rankName} &nbsp;|&nbsp; SN: <strong>{selected.serviceNumber}</strong>
                    &nbsp;|&nbsp; IPPIS: <strong>{selected.ippisNumber}</strong>
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {selected.command?.code} — {selected.command?.name} ({selected.command?.zone})
                  </Typography>
                </Box>
                <Box display="flex" gap={2} alignItems="center">
                  {statusChip(selected)}
                  <Button variant="outlined" onClick={() => setSelected(null)} sx={{ textTransform: 'none' }}>
                    Back to results
                  </Button>
                </Box>
              </Box>

              {selected.verification && (
                <Box sx={{ mt: 2, p: 2, bgcolor: 'success.50', border: '1px solid',
                           borderColor: 'success.light', borderRadius: 2 }}>
                  <Typography variant="body2" color="success.dark">
                    Verified on <strong>{showDateTime(selected.verification.verifiedAt)}</strong>
                    {selected.verification.verifiedBy && <>
                      {' '}by {selected.verification.verifiedBy.firstName} {selected.verification.verifiedBy.lastName}
                    </>}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Reference: {selected.verification.referenceNumber}
                  </Typography>
                </Box>
              )}
            </Paper>

            {/* Details */}
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>Personal</Typography>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Field label="Date of Birth" value={showDate(selected.birthDate)} />
                <Field label="Gender" value={selected.gender} />
                <Field label="Phone" value={selected.phoneNumber} />
                <Field label="Email" value={selected.email} />
              </Grid>

              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>Employment</Typography>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Field label="Grade" value={`${selected.gradeName || ''} ${selected.gradeLevel || ''}`.trim()} />
                <Field label="Step" value={selected.gradeStep} />
                <Field label="First Appointment" value={showDate(selected.dateOfFirstAppointment)} />
                <Field label="Expected Retirement" value={showDate(selected.expectedRetirementDate)} />
              </Grid>

              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>Financial</Typography>
              <Grid container spacing={2}>
                <Field label="Bank" value={selected.bankName} />
                <Field label="Account Number" value={selected.accountNumber} />
                <Field label="PFA" value={selected.pfaName} />
                <Field label="PENCOM ID" value={selected.pencomId} />
                <Field label="NHF Number" value={selected.nhfNumber} />
              </Grid>

              {selected.preClearance && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>Pre-clearance</Typography>
                  <Grid container spacing={2}>
                    <Field label="Cleared On" value={showDateTime(selected.preClearance.clearedAt)} />
                    <Field label="Cleared By"
                      value={selected.preClearance.clearingOfficerPersonnel
                        ? `${selected.preClearance.clearingOfficerPersonnel.rankName || ''} ${selected.preClearance.clearingOfficerPersonnel.firstName} ${selected.preClearance.clearingOfficerPersonnel.lastName}`.trim()
                        : selected.preClearance.clearingOfficer
                          ? `${selected.preClearance.clearingOfficer.firstName} ${selected.preClearance.clearingOfficer.lastName}`
                          : '—'} />
                  </Grid>
                </>
              )}

              {selected.complaints?.length > 0 && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    Salary Complaints ({selected.complaints.length})
                  </Typography>
                  {selected.complaints.map((c) => (
                    <Box key={c.id} sx={{ mb: 1, p: 1.5, bgcolor: 'grey.50', borderRadius: 1 }}>
                      <Typography variant="body2" fontWeight="bold">
                        {String(c.complaintType || '').replace(/_/g, ' ')}
                      </Typography>
                      {c.description && (
                        <Typography variant="caption" color="text.secondary">{c.description}</Typography>
                      )}
                    </Box>
                  ))}
                </>
              )}
            </Paper>

            {/* Verification slip — this is where the photograph lives */}
            <Paper sx={{ p: 3 }}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>Verification Slip</Typography>
              {pdfUrl ? (
                <>
                  <Box display="flex" gap={2} sx={{ mb: 2 }} flexWrap="wrap">
                    <Button variant="contained" startIcon={<PdfIcon />}
                      onClick={() => window.open(pdfUrl, '_blank')}
                      sx={{ textTransform: 'none', fontWeight: 'bold' }}>
                      Open Slip in New Tab
                    </Button>
                  </Box>
                  <Box sx={{ border: '1px solid', borderColor: 'grey.300', borderRadius: 1, overflow: 'hidden' }}>
                    <iframe title="Verification slip" src={pdfUrl}
                      style={{ width: '100%', height: 700, border: 'none' }} />
                  </Box>
                </>
              ) : (
                <Alert severity="info">
                  This officer has not been verified yet, so no slip has been issued.
                </Alert>
              )}
            </Paper>
          </>
        )}
      </Box>
    </Container>
  );
};

export default ManagementLookupPage;